import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Video, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../utils/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';

const empty = { title:'', youtubeUrl:'', category:'general', isActive:true };
const categories = { general:'General', language:'Language Learning', trade:'Trade', 'japan-life':'Life in Japan', success:'Success Stories' };
const getYtId = (url) => { const m=url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/); return m?m[1]:null; };

export default function Videos() {
  const [videos, setVideos]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modalOpen, setModalOpen]     = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId]       = useState(null);
  const [form, setForm]               = useState(empty);
  const [editing, setEditing]         = useState(false);

  const fetchVideos = async () => { setLoading(true); const {data}=await API.get('/videos'); setVideos(data); setLoading(false); };
  useEffect(()=>{ fetchVideos(); },[]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await API.put(`/videos/${form._id}`, form); toast.success('Video updated!'); }
      else         { await API.post('/videos', form);             toast.success('Video added!'); }
      setModalOpen(false); fetchVideos();
    } catch(err) { toast.error(err.response?.data?.message||'Something went wrong!'); }
  };

  const handleDelete = async () => {
    try { await API.delete(`/videos/${deleteId}`); toast.success('Video deleted!'); setConfirmOpen(false); fetchVideos(); }
    catch { toast.error('Could not delete!'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center"><Video className="text-pink-600" size={22}/></div>
          <div><h1 className="text-xl font-bold text-slate-800">Video Management</h1><p className="text-slate-500 text-sm">Manage YouTube videos</p></div>
        </div>
        <button onClick={()=>{setForm(empty);setEditing(false);setModalOpen(true);}} className="flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={18}/> Add Video
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-4 border-red-700 border-t-transparent"/></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.length===0
            ? <div className="col-span-3 bg-white rounded-xl border border-slate-100 text-center py-12 text-slate-400">No videos found. Add one!</div>
            : videos.map(v => {
              const ytId = getYtId(v.youtubeUrl);
              return (
                <div key={v._id} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                  <div className="relative">
                    {ytId ? <img src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`} alt={v.title} className="w-full h-40 object-cover"/>
                    : <div className="w-full h-40 bg-slate-100 flex items-center justify-center"><Video size={36} className="text-slate-300"/></div>}
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <a href={v.youtubeUrl} target="_blank" rel="noreferrer" className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs flex items-center gap-1">
                        <ExternalLink size={12}/> Watch on YouTube
                      </a>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-slate-800 text-sm mb-1">{v.title}</h3>
                    <span className="inline-block bg-pink-100 text-pink-700 text-xs px-2 py-0.5 rounded-full mb-3">{categories[v.category]||v.category}</span>
                    <div className="flex gap-2">
                      <button onClick={()=>{setForm(v);setEditing(true);setModalOpen(true);}} className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1 transition-colors"><Pencil size={13}/> Edit</button>
                      <button onClick={()=>{setDeleteId(v._id);setConfirmOpen(true);}} className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1 transition-colors"><Trash2 size={13}/> Delete</button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={()=>setModalOpen(false)} title={editing?'Edit Video':'Add New Video'}>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" required value={form.title} onChange={e=>setForm({...form,title:e.target.value})}/>
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">YouTube URL *</label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" required placeholder="https://www.youtube.com/watch?v=..." value={form.youtubeUrl} onChange={e=>setForm({...form,youtubeUrl:e.target.value})}/>
            {form.youtubeUrl && getYtId(form.youtubeUrl) && (
              <div className="mt-2 rounded-lg overflow-hidden border border-slate-200">
                <img src={`https://img.youtube.com/vi/${getYtId(form.youtubeUrl)}/hqdefault.jpg`} alt="preview" className="w-full h-32 object-cover"/>
              </div>
            )}
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
            <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
              {Object.entries(categories).map(([k,v])=><option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-red-600" checked={form.isActive} onChange={e=>setForm({...form,isActive:e.target.checked})}/>
            <span className="text-sm text-slate-700">Active</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={()=>setModalOpen(false)} className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium text-sm">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg font-medium text-sm">Save</button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog isOpen={confirmOpen} onConfirm={handleDelete} onCancel={()=>setConfirmOpen(false)}/>
    </div>
  );
}
